/**
 * What NumberInput does with typed text (T-176), kept apart from the component so it can be tested.
 * Text is read in the team's number format, so it round-trips what the field shows ("1,500" in an
 * en-GB team, "1.500" in a de-DE team).
 */
import { parseMoneyInput } from '@/lib/parse-money-input';

export type NumberInputRules = {
  min?: number;
  max?: number;
  /** Whole numbers only: a typed fraction is rounded when the field is left. */
  integer?: boolean;
  /** An empty field means "not set" (null). Otherwise emptying it keeps the last number. */
  allowEmpty?: boolean;
};

export type ReadNumber =
  | { kind: 'empty' }
  | { kind: 'number'; value: number }
  /** Not a number yet ("12.", "-") or not one at all ("abc"). */
  | { kind: 'invalid' };

// Digits, separators, a minus, and the spaces and apostrophes some locales group with.
const NUMBER_TEXT = /^[\d\s.,'  -]+$/;

/** Reads typed text with the team's separators. Letters (and so "1e3") are never a number. */
export function readNumberText(text: string, locale: string): ReadNumber {
  const trimmed = text.trim();
  if (trimmed === '') return { kind: 'empty' };
  if (!NUMBER_TEXT.test(trimmed)) return { kind: 'invalid' };
  const value = parseMoneyInput(trimmed, { locale, allowNegative: true });
  return value == null ? { kind: 'invalid' } : { kind: 'number', value };
}

/** A number brought inside the rules: rounded when `integer`, then held between min and max. */
export function fitNumber(value: number, rules: NumberInputRules): number {
  let next = rules.integer ? Math.round(value) : value;
  if (rules.min != null && next < rules.min) next = rules.min;
  if (rules.max != null && next > rules.max) next = rules.max;
  return next;
}

/**
 * What to tell the parent while typing: a number as typed (the form's validator says if it's out of
 * range, so Enter never submits an old value), null for an empty field that may be empty, and
 * `undefined` (nothing) otherwise. Nothing is clamped here, so typing "5" on the way to "50" in a
 * field with a minimum of 10 doesn't jump.
 */
export function valueWhileTyping(
  text: string,
  locale: string,
  rules: NumberInputRules
): number | null | undefined {
  const read = readNumberText(text, locale);
  if (read.kind === 'empty') return rules.allowEmpty ? null : undefined;
  if (read.kind === 'invalid') return undefined;
  return read.value;
}

/**
 * The value when the field is left (blur or Enter) after something was typed: the typed number
 * fitted to the rules; null when empty and allowed; otherwise the last value (`current`), so an
 * emptied or unreadable field goes back to what it held instead of a default.
 */
export function valueOnLeave(
  text: string,
  locale: string,
  rules: NumberInputRules,
  current: number | null
): number | null {
  const read = readNumberText(text, locale);
  if (read.kind === 'empty') return rules.allowEmpty ? null : current;
  if (read.kind === 'invalid') return current;
  return fitNumber(read.value, rules);
}
